import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

export type MyCompany = { id: string; name: string; logoUrl: string | null; role: string };

export const logoUrl = (path: string | null | undefined, bust?: string | null) =>
  path ? `${supabase.storage.from("org-logos").getPublicUrl(path).data.publicUrl}${bust ? `?v=${encodeURIComponent(bust)}` : ""}` : null;

let cache: Promise<MyCompany | null> | null = null;
/** Call after changing the company name/logo so every badge reloads. */
export function refreshCompanyBadge() { cache = null; window.dispatchEvent(new Event("tf-company")); }

/** The signed-in user's own company (staff: their internal company). Cached per page load. */
export function useMyCompany(userId: string) {
  const [c, setC] = useState<MyCompany | null>(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const f = () => setRefresh((n) => n + 1);
    window.addEventListener("tf-company", f);
    return () => window.removeEventListener("tf-company", f);
  }, []);
  useEffect(() => {
    cache ??= (async () => {
      const { data } = await supabase.from("memberships")
        .select("role, org_id, organisations(name, logo_path, type, updated_at)").eq("user_id", userId);
      const rows = (data ?? []) as any[];
      const pick = rows.find((r) => r.organisations?.type === "internal") ?? rows.find((r) => r.role === "org_owner") ?? rows[0];
      if (!pick?.organisations) return null;
      return { id: pick.org_id, name: pick.organisations.name, role: pick.role, logoUrl: logoUrl(pick.organisations.logo_path, pick.organisations.updated_at) };
    })();
    let live = true;
    cache.then((v) => live && setC(v));
    return () => { live = false; };
  }, [userId, refresh]);
  return c;
}

/** Company logo (or name) for the top bar, shown left of the live indicator. */
export default function CompanyBadge({ userId }: { userId: string }) {
  const c = useMyCompany(userId);
  const [broken, setBroken] = useState(false);
  if (!c) return null;
  return (
    <span className="co-badge" title={c.name}>
      {c.logoUrl && !broken ? <img src={c.logoUrl} alt={c.name} onError={() => setBroken(true)} /> : <span>{c.name}</span>}
    </span>
  );
}
